import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { logGeocodingMap } from "../utils/geocodingMapDebug";
import {
  NOMINATIM_ADDRESS_FORMAT_VERSION,
  nominatimResultToGooglePlaceFormat,
  pickCityAndCountryFromAddressComponents,
} from "../utils/nominatimGoogleAddressFormat";

// Cache for geocoding results to prevent API overuse
const geocodingCache = new Map();
const GEOCODING_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const GEOCODING_RATE_LIMIT_DELAY = 1000; // 1 second delay between requests (Nominatim requires max 1 request per second)
let lastGeocodingRequestTime = 0;

/** Versioned storage key — bump NOMINATIM_ADDRESS_FORMAT_VERSION invalidates old entries */
const GEOCODING_CACHE_STORAGE_KEY = `nominatim_geocoding_cache_v${NOMINATIM_ADDRESS_FORMAT_VERSION}`;
const LEGACY_GEOCODING_CACHE_STORAGE_KEY = "nominatim_geocoding_cache";

function versionedCacheKey(rawKey) {
  return `v${NOMINATIM_ADDRESS_FORMAT_VERSION}_${rawKey}`;
}

// Load cache from localStorage on initialization
const loadGeocodingCacheFromStorage = () => {
  try {
    const cachedData = localStorage.getItem(GEOCODING_CACHE_STORAGE_KEY);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      const now = Date.now();
      // Only load valid (non-expired) cache entries
      Object.entries(parsed).forEach(([key, value]) => {
        if (value.timestamp && (now - value.timestamp) < GEOCODING_CACHE_DURATION) {
          geocodingCache.set(key, value);
        }
      });
      console.log(`[Nominatim Geocoding Service] Loaded ${geocodingCache.size} cached entries from localStorage`);
    }
  } catch (error) {
    console.error('[Nominatim Geocoding Service] Error loading cache from localStorage:', error);
  }
};

// Save cache to localStorage
const saveGeocodingCacheToStorage = () => {
  try {
    const cacheObject = {};
    geocodingCache.forEach((value, key) => {
      cacheObject[key] = value;
    });
    localStorage.setItem(GEOCODING_CACHE_STORAGE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('[Nominatim Geocoding Service] Error saving cache to localStorage:', error);
    // If storage is full, try to clean old entries
    try {
      const now = Date.now();
      const cleanedCache = {};
      geocodingCache.forEach((value, key) => {
        if (value.timestamp && (now - value.timestamp) < GEOCODING_CACHE_DURATION) {
          cleanedCache[key] = value;
        }
      });
      localStorage.setItem(GEOCODING_CACHE_STORAGE_KEY, JSON.stringify(cleanedCache));
      geocodingCache.clear();
      Object.entries(cleanedCache).forEach(([key, value]) => {
        geocodingCache.set(key, value);
      });
    } catch (cleanError) {
      console.error('[Nominatim Geocoding Service] Error cleaning cache:', cleanError);
    }
  }
};

// Initialize cache from localStorage
loadGeocodingCacheFromStorage();

// Clean expired cache entries periodically (every hour)
setInterval(() => {
  const now = Date.now();
  let cleanedCount = 0;
  geocodingCache.forEach((value, key) => {
    if (value.timestamp && (now - value.timestamp) >= GEOCODING_CACHE_DURATION) {
      geocodingCache.delete(key);
      cleanedCount++;
    }
  });
  if (cleanedCount > 0) {
    console.log(`[Nominatim Geocoding Service] Cleaned ${cleanedCount} expired entries`);
    saveGeocodingCacheToStorage();
  }
}, 60 * 60 * 1000); // Run every hour

/**
 * Get geocoding data for an address using Nominatim (free, no API key needed)
 * @param {string} address - The address to geocode
 * @param {string} language - Language code (default: 'he')
 * @returns {Promise<Object|null>} - Geocoding result in Google Maps format or null if failed
 */
export const getGeocodingByAddress = async (address, language = 'he') => {
  if (!address || !address.trim()) {
    return null;
  }

  // Check cache first (both in-memory and localStorage)
  const cacheKey = versionedCacheKey(`${address.toLowerCase().trim()}_${language}`);
  const cached = geocodingCache.get(cacheKey);
  const now = Date.now();
  
  if (cached && (now - cached.timestamp) < GEOCODING_CACHE_DURATION) {
    console.log(`[Nominatim Geocoding Service] Cache HIT for: ${address}`);
    return cached.data;
  }

  // If cache expired, remove it
  if (cached && (now - cached.timestamp) >= GEOCODING_CACHE_DURATION) {
    geocodingCache.delete(cacheKey);
    saveGeocodingCacheToStorage();
  }

  console.log(`[Nominatim Geocoding Service] Cache MISS for: ${address} - making API call`);

  // Rate limiting - Nominatim requires max 1 request per second
  const timeSinceLastRequest = now - lastGeocodingRequestTime;
  const delay = timeSinceLastRequest < GEOCODING_RATE_LIMIT_DELAY 
    ? GEOCODING_RATE_LIMIT_DELAY - timeSinceLastRequest 
    : 0;

  return new Promise((resolve) => {
    setTimeout(async () => {
      lastGeocodingRequestTime = Date.now();
      
      try {
        // Use Nominatim geocoding API (free, no API key needed)
        const encodedAddress = encodeURIComponent(address);
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=3&accept-language=${language}&addressdetails=1`,
          {
            headers: {
              'Accept': 'application/json',
              'Accept-Language': language,
              'User-Agent': 'HotelsRiver-FE/1.0 (Hotel search; contact@hotelsriver.com)'
            }
          }
        );
        
        if (!response.ok) {
          console.error("Nominatim geocoding request failed:", response.status);
          resolve(null);
          return;
        }

        const data = await response.json();

        if (data && data.length > 0) {
          let googleFormatResult = null;
          for (const result of data) {
            googleFormatResult = nominatimResultToGooglePlaceFormat(result);
            if (googleFormatResult) break;
          }
          if (googleFormatResult) {
            logGeocodingMap("forward_nominatim", {
              query: address,
              language,
              rawAddress: data[0]?.address ?? null,
              display_name: data[0]?.display_name ?? null,
              address_components: googleFormatResult.address_components,
              parsed: pickCityAndCountryFromAddressComponents(
                googleFormatResult.address_components
              ),
              lat: googleFormatResult.geometry?.location?.lat?.(),
              lon: googleFormatResult.geometry?.location?.lng?.(),
            });
            // Cache the successful result (both in-memory and localStorage)
            const cacheEntry = {
              data: googleFormatResult,
              timestamp: Date.now()
            };
            geocodingCache.set(cacheKey, cacheEntry);
            saveGeocodingCacheToStorage();
            console.log(`[Nominatim Geocoding Service] Cached result for: ${address}`);
            resolve(googleFormatResult);
          } else {
            console.error("Failed to convert Nominatim result to Google format.");
            resolve(null);
          }
        } else {
          // No results found - cache null to avoid repeated API calls
          const cacheEntry = {
            data: null,
            timestamp: Date.now()
          };
          geocodingCache.set(cacheKey, cacheEntry);
          saveGeocodingCacheToStorage();
          console.log(`[Nominatim Geocoding Service] No results found for: ${address}`);
          resolve(null);
        }
      } catch (error) {
        console.error("Network error during geocoding:", error);
        resolve(null);
      }
    }, delay);
  });
};

/**
 * Reverse geocoding via HotelsRiver backend (no direct Nominatim from browser).
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} language - Language code (default: 'en')
 * @returns {Promise<{
 *   ok: boolean,
 *   status: 'success'|'not_found'|'bad_request'|'provider_error'|'network_error'|'rate_limited',
 *   data: object|null,
 *   error: string|null
 * }>}
 */
export const getGeocodingByCoordinates = async (lat, lng, language = 'en') => {
  const latN = typeof lat === 'function' ? lat() : lat;
  const lngN = typeof lng === 'function' ? lng() : lng;
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) {
    return {
      ok: false,
      status: 'bad_request',
      error: 'Invalid latitude or longitude',
      data: null,
    };
  }

  const cacheKey = versionedCacheKey(`reverse_${latN}_${lngN}_${language}`);
  const cached = geocodingCache.get(cacheKey);
  const now = Date.now();

  if (cached && (now - cached.timestamp) < GEOCODING_CACHE_DURATION) {
    console.log(`[Geocoding Service] Cache HIT reverse: ${latN}, ${lngN}`);
    return {
      ok: true,
      status: 'success',
      error: null,
      data: cached.data,
    };
  }

  if (cached && (now - cached.timestamp) >= GEOCODING_CACHE_DURATION) {
    geocodingCache.delete(cacheKey);
    saveGeocodingCacheToStorage();
  }

  console.log(`[Geocoding Service] Cache MISS reverse: ${latN}, ${lngN} — calling backend`);

  const timeSinceLastRequest = now - lastGeocodingRequestTime;
  const delay =
    timeSinceLastRequest < GEOCODING_RATE_LIMIT_DELAY
      ? GEOCODING_RATE_LIMIT_DELAY - timeSinceLastRequest
      : 0;

  return new Promise((resolve) => {
    setTimeout(async () => {
      lastGeocodingRequestTime = Date.now();
      const url = resolveApiUrl('/api/reverse-geocode');
      try {
        const response = await axios.post(
          url,
          { lat: latN, lng: lngN, language: language || 'en' },
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
            validateStatus: () => true,
            timeout: 30000,
          }
        );

        const body = response.data;
        if (body && typeof body === 'object' && 'ok' in body && body.status) {
          if (body.ok === true && body.status === 'success' && body.data) {
            logGeocodingMap('reverse_backend', {
              lat: latN,
              lng: lngN,
              language,
              rawCity: body.data.city,
              rawState: body.data.state,
              address_components: body.data.address_components,
              parsed: pickCityAndCountryFromAddressComponents(body.data.address_components),
            });
            const cacheEntry = { data: body.data, timestamp: Date.now() };
            geocodingCache.set(cacheKey, cacheEntry);
            saveGeocodingCacheToStorage();
            console.log(`[Geocoding Service] Cached reverse result for: ${latN}, ${lngN}`);
          }
          resolve({
            ok: Boolean(body.ok),
            status: body.status,
            error: body.error ?? null,
            data: body.data ?? null,
          });
          return;
        }

        resolve({
          ok: false,
          status: 'provider_error',
          error: 'Unexpected response from reverse geocode API',
          data: null,
        });
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          'Reverse geocoding request failed';
        console.error('[Geocoding Service] reverse geocode request failed:', error);
        resolve({
          ok: false,
          status: 'network_error',
          error: String(msg),
          data: null,
        });
      }
    }, delay);
  });
};

/**
 * Clear the geocoding cache (useful for testing or manual cleanup)
 */
export const clearGeocodingCache = () => {
  geocodingCache.clear();
  try {
    localStorage.removeItem(GEOCODING_CACHE_STORAGE_KEY);
    localStorage.removeItem(LEGACY_GEOCODING_CACHE_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
  console.log('[Nominatim Geocoding Service] Cache cleared');
};

/**
 * Get cache statistics
 */
export const getGeocodingCacheStats = () => {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;
  
  geocodingCache.forEach((value) => {
    if (value.timestamp && (now - value.timestamp) < GEOCODING_CACHE_DURATION) {
      validEntries++;
    } else {
      expiredEntries++;
    }
  });
  
  return {
    total: geocodingCache.size,
    valid: validEntries,
    expired: expiredEntries
  };
};

